import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { locations } from '../utils/mock';
import PageTitle from '../hooks/PageTitle';

const Locations = () => {
  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="Our Locations" />
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-serif mb-6">
              Our Global Presence
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Strategic offices in key financial centres worldwide to serve our international client base
            </p>
          </div>
        </div>
      </section>

      {/* Locations Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {locations.map((location) => (
              <Card 
                key={location.id} 
                className={`border-0 shadow-lg hover:shadow-2xl transition-all duration-300 ${
                  location.isPrimary ? 'ring-2 ring-gray-900' : ''
                }`}
              >
                <CardContent className="p-8">
                  {location.isPrimary && (
                    <Badge className="mb-4 bg-gray-900 text-white">Headquarters</Badge>
                  )}
                  
                  <h3 className="text-3xl font-serif text-gray-900 mb-2">{location.city}</h3>
                  <p className="text-gray-500 mb-6">{location.country}</p>
                  
                  <p className="text-gray-600 leading-relaxed mb-6">{location.description}</p>
                  
                  <div className="space-y-4 border-t border-gray-200 pt-6">
                    <div className="flex items-start">
                      <MapPin className="h-5 w-5 text-gray-400 mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-600">{location.address}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <a href={`tel:${location.phone}`} className="text-gray-600 hover:text-gray-900 transition-colors">
                        {location.phone}
                      </a>
                    </div>
                    
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <a href={`mailto:${location.email}`} className="text-gray-600 hover:text-gray-900 transition-colors">
                        {location.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">Global Reach, Local Expertise</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our international network enables us to provide seamless legal services across borders
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center relative overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1587702068694-a909ef4aa346"
                alt="Global offices"
                className="absolute inset-0 w-full h-full object-cover opacity-40"
              />
              <div className="relative z-10 text-center">
                <p className="text-gray-600 text-lg font-medium mb-2">Serving clients across</p>
                <p className="text-5xl font-serif text-gray-900">25+ Jurisdictions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Hours */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-10">
              <div className="flex items-start space-x-6">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-serif text-gray-900 mb-4">Office Hours & Availability</h3>
                  <div className="space-y-3 text-gray-600">
                    <p>
                      <span className="font-medium">London:</span> Monday - Friday, 9:00 AM - 6:00 PM GMT
                    </p>
                    <p>
                      <span className="font-medium">Luxembourg:</span> Monday - Friday, 9:00 AM - 6:00 PM CET
                    </p>
                    <p>
                      <span className="font-medium">Geneva (Switzerland):</span> Monday - Friday, 9:00 AM - 6:00 PM CET
                    </p>
                    <p>
                      <span className="font-medium">Dubai:</span> Sunday - Thursday, 9:00 AM - 6:00 PM GST
                    </p>
                    <p className="pt-4 border-t border-gray-200 mt-4 text-sm">
                      For urgent matters outside office hours, please contact your designated partner directly. Emergency assistance is available 24/7 for existing clients.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif mb-6">Visit Us or Get In Touch</h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            We welcome the opportunity to meet with you at any of our offices
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-gray-900 bg-white rounded-md hover:bg-gray-100 transition-colors"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default Locations;
